import React, { useState, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
  Area,
  AreaChart,
} from 'recharts';
import {
  Activity,
  Award,
  BarChart2,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Flame,
  Search,
  Star,
  Target,
  Trophy,
  TrendingUp,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';

// ─── Muscle group color palette ───────────────────────────────────────────────
const MUSCLE_COLORS: Record<string, string> = {
  Chest: '#22c55e',
  Back: '#3b82f6',
  Legs: '#f59e0b',
  Shoulders: '#a855f7',
  Arms: '#ef4444',
  Core: '#06b6d4',
  Other: '#6b7280',
};

const MILESTONE_LABELS: Record<number, { label: string; icon: string; color: string }> = {
  100_000: { label: '100K', icon: '🏅', color: '#cd7f32' },
  500_000: { label: '500K', icon: '🥈', color: '#c0c0c0' },
  1_000_000: { label: '1M', icon: '🥇', color: '#ffd700' },
  5_000_000: { label: '5M', icon: '🏆', color: '#22c55e' },
  10_000_000: { label: '10M', icon: '👑', color: '#a855f7' },
};

// ─── Custom chart elements ────────────────────────────────────────────────────
const CustomDot = (props: any) => {
  const { cx, cy, payload } = props;
  if (!cx || !cy) return null;
  if (payload?.isPR) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={9} fill="#f59e0b" opacity={0.2} />
        <Star x={cx - 7} y={cy - 7} width={14} height={14} fill="#f59e0b" stroke="#f59e0b" strokeWidth={1} />
      </g>
    );
  }
  return <circle cx={cx} cy={cy} r={3} fill={props.stroke} strokeWidth={0} />;
};

const HeroTooltip = ({ active, payload, label, mode }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono shadow-xl">
      <div className="text-muted-foreground mb-1">{label}</div>
      <div className="text-foreground font-bold text-sm">
        {mode === 'overload' ? `${d.value} LBS (1RM)` : `${d.value.toLocaleString()} LBS vol`}
      </div>
      <div className="text-muted-foreground">
        {d.bestWeight} LBS × {d.bestReps} REPS
      </div>
      {d.isPR && <div className="text-amber-400 mt-0.5 font-bold">★ PR SESSION</div>}
    </div>
  );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({
  icon,
  label,
  value,
  sub,
  accent = false,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
}) => (
  <div className={`forge-panel p-4 flex flex-col gap-1 ${accent ? 'border-primary/40' : ''}`}>
    <div className="flex items-center gap-1.5 text-muted-foreground">
      {icon}
      <span className="text-[10px] font-mono uppercase tracking-wider">{label}</span>
    </div>
    <div className={`font-mono font-bold text-2xl ${accent ? 'text-primary' : 'text-foreground'}`}>{value}</div>
    {sub && <div className="text-[10px] font-mono text-muted-foreground">{sub}</div>}
  </div>
);

// ─── PR Hall of Fame grouped by muscle ───────────────────────────────────────
const PRHallOfFame = ({ prs, exercises }: { prs: any[]; exercises: any[] }) => {
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const map: Record<string, { exerciseName: string; exerciseId: string; bestWeight: number; bestReps: number; category: string }[]> = {};
    for (const pr of prs) {
      const ex = exercises.find((e) => e.id === pr.exerciseId);
      if (!ex) continue;
      const cat = ex.category as string;
      if (!map[cat]) map[cat] = [];
      const existing = map[cat].find((p) => p.exerciseId === pr.exerciseId);
      if (!existing) {
        map[cat].push({
          exerciseName: ex.name,
          exerciseId: pr.exerciseId,
          bestWeight: pr.bestWeight ?? 0,
          bestReps: pr.bestReps ?? 0,
          category: cat,
        });
      } else {
        if ((pr.bestWeight ?? 0) > existing.bestWeight) {
          existing.bestWeight = pr.bestWeight ?? 0;
          existing.bestReps = pr.bestReps ?? 0;
        }
      }
    }
    return Object.entries(map).sort((a, b) => b[1].length - a[1].length);
  }, [prs, exercises]);

  if (grouped.length === 0) {
    return (
      <div className="py-10 text-center border border-dashed border-border/40 rounded-sm">
        <Trophy className="h-8 w-8 text-muted-foreground/20 mx-auto mb-3" />
        <p className="text-xs font-mono text-muted-foreground uppercase">No PRs Recorded Yet</p>
        <p className="text-[11px] text-muted-foreground/60 mt-1">Complete sessions to start building your hall of fame</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {grouped.map(([category, items]) => {
        const isOpen = expandedGroup === category;
        const color = MUSCLE_COLORS[category] ?? '#6b7280';
        return (
          <div key={category} className="forge-panel overflow-hidden">
            <button
              className="w-full flex items-center justify-between p-3 hover:bg-secondary/20 transition-colors"
              onClick={() => setExpandedGroup(isOpen ? null : category)}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-2 h-6 rounded-full" style={{ backgroundColor: color }} />
                <span className="font-mono font-bold text-sm uppercase tracking-wide text-foreground">{category}</span>
                <span
                  className="font-mono text-[10px] px-1.5 py-0.5 rounded-sm border"
                  style={{ color, borderColor: `${color}40`, backgroundColor: `${color}15` }}
                >
                  {items.length} PRs
                </span>
              </div>
              {isOpen
                ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                : <ChevronDown className="h-4 w-4 text-muted-foreground" />
              }
            </button>

            {isOpen && (
              <div className="border-t border-border/30 divide-y divide-border/20">
                {items.sort((a, b) => b.bestWeight - a.bestWeight).map((item) => (
                  <div key={item.exerciseId} className="flex items-center justify-between px-4 py-3">
                    <div className="min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">{item.exerciseName}</div>
                      <div className="text-[10px] font-mono text-muted-foreground uppercase mt-0.5">{item.category}</div>
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <div className="font-mono font-bold text-lg" style={{ color }}>{item.bestWeight} <span className="text-xs text-muted-foreground font-normal">LBS</span></div>
                      <div className="text-[10px] font-mono text-muted-foreground">× {item.bestReps} REPS</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─── Volume Milestones Timeline ───────────────────────────────────────────────
const VolumeMilestonesTimeline = () => {
  const { data } = trpc.telemetry.getVolumeMilestones.useQuery(undefined, { staleTime: 60_000 });

  if (!data) return null;

  const { milestones, currentVolumeLbs } = data;

  return (
    <div className="forge-panel p-4 space-y-4">
      <div className="forge-header">
        <span>Volume Milestones</span>
        <span className="text-primary font-mono flex items-center gap-1 text-xs">
          <Flame className="h-3.5 w-3.5" /> LIFETIME
        </span>
      </div>

      <div className="text-center py-2">
        <div className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Total Lifetime Volume</div>
        <div className="font-mono font-bold text-3xl text-primary">
          {currentVolumeLbs >= 1_000_000
            ? `${(currentVolumeLbs / 1_000_000).toFixed(2)}M`
            : currentVolumeLbs >= 1_000
            ? `${(currentVolumeLbs / 1_000).toFixed(1)}K`
            : currentVolumeLbs.toLocaleString()}{' '}
          <span className="text-sm text-muted-foreground font-normal">LBS</span>
        </div>
      </div>

      <div className="space-y-3">
        {milestones.map((m) => {
          const meta = MILESTONE_LABELS[m.milestone];
          const isAchieved = !!m.achievedDate;
          return (
            <div key={m.milestone} className={`p-3 rounded-sm border ${isAchieved ? 'border-primary/30 bg-primary/5' : 'border-border/40 bg-secondary/20'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{meta?.icon ?? '🎯'}</span>
                  <div>
                    <div className={`font-mono font-bold text-sm ${isAchieved ? 'text-primary' : 'text-muted-foreground'}`}>
                      {meta?.label ?? m.milestone.toLocaleString()} LBS
                    </div>
                    {isAchieved && m.achievedDate && (
                      <div className="text-[10px] font-mono text-muted-foreground">
                        Achieved {new Date(m.achievedDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                    )}
                    {!isAchieved && (
                      <div className="text-[10px] font-mono text-muted-foreground">{m.progressPct}% complete</div>
                    )}
                  </div>
                </div>
                {isAchieved && (
                  <Badge className="font-mono text-[9px] bg-primary/20 border-primary/40 text-primary uppercase">Unlocked</Badge>
                )}
              </div>
              {!isAchieved && (
                <div className="h-1.5 bg-secondary/60 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary/60 rounded-full transition-all duration-700"
                    style={{ width: `${m.progressPct}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Hero Exercise Progression Chart ─────────────────────────────────────────
const HeroProgressionChart = ({ exercises }: { exercises: any[] }) => {
  const [search, setSearch] = useState('');
  const [selectedExercise, setSelectedExercise] = useState<{ id: string; name: string } | null>(null);
  const [mode, setMode] = useState<'overload' | 'hypertrophy'>('overload');
  const [showDropdown, setShowDropdown] = useState(false);

  const filtered = useMemo(() => {
    if (!search.trim()) return exercises.slice(0, 10);
    return exercises.filter((e) => e.name.toLowerCase().includes(search.toLowerCase())).slice(0, 10);
  }, [search, exercises]);

  const { data: overloadData = [], isLoading: loadingOverload } = trpc.prs.getExerciseProgression.useQuery(
    { exerciseId: selectedExercise?.id ?? '', trainingMode: 'overload' },
    { enabled: !!selectedExercise, staleTime: 30_000 }
  );
  const { data: hypertrophyData = [], isLoading: loadingHypertrophy } = trpc.prs.getExerciseProgression.useQuery(
    { exerciseId: selectedExercise?.id ?? '', trainingMode: 'hypertrophy' },
    { enabled: !!selectedExercise, staleTime: 30_000 }
  );

  const activeData = mode === 'overload' ? overloadData : hypertrophyData;
  const isLoading = mode === 'overload' ? loadingOverload : loadingHypertrophy;
  const lineColor = mode === 'overload' ? '#22c55e' : '#f59e0b';
  const gradientId = `heroGradient-${mode}`;

  const hasOverload = overloadData.length >= 2;
  const hasHypertrophy = hypertrophyData.length >= 2;
  const hasBoth = hasOverload && hasHypertrophy;

  const allTimeBest = useMemo(() => {
    if (!activeData.length) return null;
    return activeData.reduce((max, d) => (d.value > max.value ? d : max), activeData[0]);
  }, [activeData]);

  const formatDate = (d: string) => {
    const dt = new Date(d + 'T00:00:00');
    return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getDate()}`;
  };

  return (
    <div className="forge-panel p-4 space-y-4">
      <div className="forge-header">
        <span>Exercise Progression</span>
        <span className="text-primary font-mono flex items-center gap-1 text-xs">
          <BarChart2 className="h-3.5 w-3.5" /> HERO CHART
        </span>
      </div>

      {/* Exercise search */}
      <div className="relative">
        <div className="flex items-center gap-2 bg-secondary/40 border border-border/60 rounded-sm px-3 h-9">
          <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <input
            type="text"
            placeholder="Search exercise..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            className="flex-1 bg-transparent text-sm font-mono text-foreground placeholder:text-muted-foreground/60 outline-none"
          />
          {selectedExercise && (
            <button
              onClick={() => { setSelectedExercise(null); setSearch(''); }}
              className="text-muted-foreground hover:text-foreground text-xs font-mono"
            >
              ✕
            </button>
          )}
        </div>

        {showDropdown && search && filtered.length > 0 && (
          <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-background border border-border rounded-sm shadow-xl max-h-48 overflow-y-auto">
            {filtered.map((ex) => (
              <button
                key={ex.id}
                className="w-full text-left px-3 py-2 text-sm hover:bg-secondary/60 transition-colors flex items-center justify-between"
                onClick={() => {
                  setSelectedExercise({ id: ex.id, name: ex.name });
                  setSearch(ex.name);
                  setShowDropdown(false);
                }}
              >
                <span className="font-medium text-foreground truncate">{ex.name}</span>
                <span className="text-[10px] font-mono text-muted-foreground ml-2 shrink-0">{ex.category}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {!selectedExercise ? (
        <div className="h-[220px] flex flex-col items-center justify-center border border-dashed border-border/40 rounded-sm">
          <TrendingUp className="h-8 w-8 text-muted-foreground/20 mb-3" />
          <p className="text-xs font-mono text-muted-foreground uppercase">Select an exercise to view progression</p>
          <p className="text-[11px] text-muted-foreground/60 mt-1">Search by name above</p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Mode toggle */}
          {hasBoth && (
            <div className="flex items-center gap-1 bg-secondary/50 rounded-sm p-0.5 w-fit">
              <button
                onClick={() => setMode('overload')}
                className={`px-3 py-1 text-[10px] font-mono rounded-sm transition-colors ${
                  mode === 'overload'
                    ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                OVERLOAD
              </button>
              <button
                onClick={() => setMode('hypertrophy')}
                className={`px-3 py-1 text-[10px] font-mono rounded-sm transition-colors ${
                  mode === 'hypertrophy'
                    ? 'bg-amber-500/20 text-amber-400 border border-amber-500/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                HYPERTROPHY
              </button>
            </div>
          )}

          {isLoading ? (
            <div className="h-[220px] flex items-center justify-center">
              <div className="text-xs font-mono text-muted-foreground animate-pulse uppercase">Loading...</div>
            </div>
          ) : activeData.length < 2 ? (
            <div className="h-[160px] flex flex-col items-center justify-center border border-dashed border-border/40 rounded-sm">
              <Activity className="h-6 w-6 text-muted-foreground/30 mb-2" />
              <p className="text-xs font-mono text-muted-foreground uppercase">Not enough data yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">Log 2+ sessions to see progression</p>
            </div>
          ) : (
            <>
              {/* Hero chart with gradient fill */}
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activeData} margin={{ top: 10, right: 8, left: -16, bottom: 0 }}>
                    <defs>
                      <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={lineColor} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={lineColor} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                      axisLine={false}
                      tickLine={false}
                      width={48}
                      tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                    />
                    <Tooltip
                      content={<HeroTooltip mode={mode} />}
                      cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="value"
                      stroke={lineColor}
                      strokeWidth={2.5}
                      fill={`url(#${gradientId})`}
                      dot={<CustomDot stroke={lineColor} />}
                      activeDot={{ r: 5, fill: lineColor }}
                    />
                    {/* PR reference dots */}
                    {activeData
                      .filter((d) => d.isPR)
                      .map((d, i) => (
                        <ReferenceDot
                          key={i}
                          x={d.date}
                          y={d.value}
                          r={0}
                          label={{
                            value: '★',
                            position: 'top',
                            fill: '#f59e0b',
                            fontSize: 12,
                          }}
                        />
                      ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* All-time best callout */}
              {allTimeBest && (
                <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-sm px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <Star className="h-4 w-4 text-amber-400 shrink-0" />
                    <span className="text-xs font-mono text-muted-foreground uppercase">All-Time Best</span>
                  </div>
                  <div className="text-right">
                    <span className="font-mono font-bold text-primary text-sm">
                      {mode === 'overload' ? `${allTimeBest.value} LBS (1RM)` : `${allTimeBest.value.toLocaleString()} LBS vol`}
                    </span>
                    <div className="text-[10px] font-mono text-muted-foreground">
                      {allTimeBest.bestWeight} × {allTimeBest.bestReps} · {formatDate(allTimeBest.date)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
interface TelemetryLegacyModeProps {
  refreshTrigger: number;
}

export default function TelemetryLegacyMode({ refreshTrigger }: TelemetryLegacyModeProps) {
  const { data: lifetimeStats, isLoading: statsLoading } = trpc.telemetry.getLifetimeStats.useQuery(undefined, {
    staleTime: 60_000,
  });
  const { data: rawPRs = [] } = trpc.prs.list.useQuery(undefined, { staleTime: 30_000 });
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery(undefined, { staleTime: 60_000 });

  const prs = rawPRs.map((pr) => ({
    exerciseId: pr.exerciseId,
    bestWeight: pr.weight ?? 0,
    bestReps: pr.reps ?? 0,
  }));

  const exercises = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    trackingMode: e.trackingMode,
  }));

  const formatVolume = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
    return v.toLocaleString();
  };

  return (
    <div className="space-y-5">

      {/* ── Lifetime Stats Row ─────────────────────────────────────────────── */}
      {statsLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 animate-pulse">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="forge-panel p-4 h-20 bg-secondary/20" />
          ))}
        </div>
      ) : lifetimeStats ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatCard
            icon={<Dumbbell className="h-3.5 w-3.5" />}
            label="Total Sessions"
            value={lifetimeStats.totalSessions.toLocaleString()}
            sub="completed workouts"
            accent
          />
          <StatCard
            icon={<Flame className="h-3.5 w-3.5" />}
            label="Total Volume"
            value={formatVolume(lifetimeStats.totalVolumeLbs)}
            sub="lbs lifted lifetime"
            accent
          />
          <StatCard
            icon={<Zap className="h-3.5 w-3.5" />}
            label="Total Sets"
            value={lifetimeStats.totalSets.toLocaleString()}
            sub="working sets logged"
          />
          <StatCard
            icon={<Trophy className="h-3.5 w-3.5" />}
            label="PRs Set"
            value={lifetimeStats.totalPRs.toLocaleString()}
            sub="personal records"
          />
          <StatCard
            icon={<Target className="h-3.5 w-3.5" />}
            label="Longest Streak"
            value={`${lifetimeStats.longestStreak}d`}
            sub="consecutive days"
          />
        </div>
      ) : null}

      {/* ── Hero Exercise Progression Chart ──────────────────────────────── */}
      <HeroProgressionChart exercises={exercises} />

      {/* ── PR Hall of Fame ───────────────────────────────────────────────── */}
      <div className="forge-panel p-4 space-y-4">
        <div className="forge-header">
          <span>PR Hall of Fame</span>
          <span className="text-amber-400 font-mono flex items-center gap-1 text-xs">
            <Award className="h-3.5 w-3.5" /> ALL TIME
          </span>
        </div>
        <PRHallOfFame prs={prs} exercises={exercises} />
      </div>

      {/* ── Volume Milestones ─────────────────────────────────────────────── */}
      <VolumeMilestonesTimeline />

    </div>
  );
}
